import { useEffect, useRef, useState } from 'react';

import { getLessonDisplaySize } from '../../../helpers/getLessonDisplaySize';
import { getLessonSizeFromSrc } from '../../../helpers/getLessonSizeFromSrc';

type TProps = {
  src: string;
};

export const TrainingLessonPlayer = ({ src }: TProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const lessonSize = getLessonSizeFromSrc(src);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const updateWidth = () => {
      setContainerWidth(element.clientWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  if (!lessonSize) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">
        Unable to load this lesson.{' '}
        <a href={src} target="_blank" rel="noreferrer" className="text-[var(--color-link)] hover:underline">
          Open it in a new tab
        </a>
        .
      </p>
    );
  }

  const { scale, width, height } = getLessonDisplaySize(containerWidth, lessonSize);

  if (scale <= 0) {
    return <div ref={containerRef} className="w-full" />;
  }

  return (
    <div ref={containerRef} className="flex w-full justify-center">
      <div className="relative shrink-0 overflow-hidden" style={{ width, height }}>
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{
            width: lessonSize.width,
            height: lessonSize.height,
            transform: `scale(${scale})`,
          }}
        >
          <iframe
            title="Training lesson"
            src={src}
            className="block h-full w-full border-0"
            scrolling="no"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
            allow="fullscreen"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};
